import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE } from '../../constants/general.constants';
import { GlobalRole, Permission } from '@app/shared/interfaces';

@Injectable({
  providedIn: 'root'
})
export class RoleService {
  private http = inject(HttpClient);

  private readonly rolesEndpoint = `${API_BASE}/roles`;
  private readonly permissionsEndpoint = `${API_BASE}/permissions`;

  /**
   * Get a list of global roles
   * @returns Observable<GlobalRole[]>
   */
  get(): Observable<GlobalRole[]> {
    return this.http.get<GlobalRole[]>(this.rolesEndpoint);
  }

  /**
   * Get a list of global roles (alias for compatibility)
   * @returns Observable<GlobalRole[]>
   */
  getGlobalRoles(): Observable<GlobalRole[]> {
    return this.get();
  }

  /**
   * Create a new global role
   * @param newRole - The role to create
   * @returns Observable<GlobalRole>
   */
  create(newRole: GlobalRole): Observable<GlobalRole> {
    return this.http.post<GlobalRole>(this.rolesEndpoint, newRole);
  }

  /**
   * Update an existing global role
   * @param roleId - The ID of the role to update
   * @param updatedRole - The updated role data
   * @returns Observable<GlobalRole>
   */
  update(roleId: number, updatedRole: GlobalRole): Observable<GlobalRole> {
    const roleEndpoint = `${this.rolesEndpoint}/${roleId}`;
    return this.http.put<GlobalRole>(roleEndpoint, updatedRole);
  }

  /**
   * Delete a global role
   * @param roleId - The ID of the role to delete
   * @returns Observable<void>
   */
  delete(roleId: number): Observable<void> {
    const roleEndpoint = `${this.rolesEndpoint}/${roleId}`;
    return this.http.delete<void>(roleEndpoint);
  }

  /**
   * Get list of available permissions
   * @returns Observable<Permission[]>
   */
  getPermissions(): Observable<Permission[]> {
    return this.http.get<Permission[]>(this.permissionsEndpoint);
  }
}
